import React from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, CircularProgress } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useLoadLeagueData } from '../../../../../app/dataLoaderHooks';
import { League } from '../../../../../sdk/model';
import { ChartDataPoint } from '../types';
import { RosterManagementCharts } from './RosterManagementCharts';

interface RosterManagementChartsWithDataProps {
  league: League;
  chartData: ChartDataPoint[];
}

export function RosterManagementChartsWithData({ league: baseLeague, chartData }: RosterManagementChartsWithDataProps) {
  const { leagueId } = useParams();
  const user = useAppSelector((state) => state.user);
  
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);

  // Load league data with roster information
  const leagueWithRoster = useLoadLeagueData({
    leagueId: leagueId, 
    userId: user.id, 
    loadBasicAuctionData: false, 
    loadFullAuctionData: false, 
    loadContractData: true,  // Need contracts for salary cap data
    loadRosterData: true,    // Load all roster data
    loadBasicDraftData: false, 
    loadFullDraftData: false, 
    loadRosterFor: [],       // Empty array since we're loading all roster data
    loadWaivedContracts: false,
    loadScheduleData: false,
    setLoadComplete: setLoadComplete, 
    isLoadComplete: isLoadComplete,
    setIsException: setIsException, 
    isException: isException
  });

  if (isException) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography color="error">Error loading roster data</Typography>
      </Box>
    );
  }

  if (!isLoadComplete || !leagueWithRoster) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading roster data...</Typography>
      </Box>
    );
  }

  // Check if all teams have roster data loaded
  const allRostersLoaded = leagueWithRoster.teams?.every(team => team.rosterLoaded) || false;

  if (!allRostersLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading team rosters...</Typography>
      </Box>
    );
  }

  return <RosterManagementCharts league={leagueWithRoster} chartData={chartData} />;
}
